// src/components/Teabates/TeabateArgument.tsx
import React from 'react';
import { ThumbsUp, ThumbsDown, CornerUpRight } from 'lucide-react';
import api from 'config/axiosConfig';
import { useUser } from 'util/UserContext';
import { TeabateArgumentObject } from './types';
import { apiRoutes } from "util/apiRoutes";

interface TeabateArgumentProps {
  argument: TeabateArgumentObject;
  onReply: (argument: TeabateArgumentObject) => void;
  onVote: (type: 'upvote' | 'downvote', argumentId: number) => void;
}

const TeabateArgument: React.FC<TeabateArgumentProps> = ({ 
  argument, 
  onReply,
  onVote 
}) => {
  const { currentUser, setShowAuthModal } = useUser();
  
  const handleVote = async (type: 'upvote' | 'downvote') => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    try {
      await api.post(apiRoutes.votes.comment(argument.id), { value: type === "upvote" ? 1 : -1 });
      onVote(type, argument.id);
    } catch (error) {
      console.error('Error voting on argument:', error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } catch {
      return 'Invalid date';
    }
  };

  const isUpvoted = argument.user_vote?.rel_type === 'user_upvote';
  const isDownvoted = argument.user_vote?.rel_type === 'user_downvote';
  
  return (
    <div className="border-l-4 border-blue-500 pl-4 py-2 mb-3">
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center">
          <div className="text-sm font-medium text-white mr-2">
            {argument.username || '[anonymous]'}
          </div>
          <div className="text-xs text-gray-400">
            {formatDate(argument.created_at)}
          </div>
        </div>
        <div className="text-xs text-blue-400">
          {argument.properties.argument_type}
        </div>
      </div>
      
      <div
        className="text-sm text-white mb-2"
        dangerouslySetInnerHTML={{ __html: argument.content }}
      />
      
      <div className="flex items-center pt-1">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => handleVote('upvote')}
            className={`flex items-center space-x-1 text-xs ${isUpvoted ? 'text-blue-400' : 'text-gray-400'}`}
          >
            <ThumbsUp size={14} />
            <span>{argument.upvotes || 0}</span>
          </button>
          <button 
            onClick={() => handleVote('downvote')}
            className={`flex items-center space-x-1 text-xs ${isDownvoted ? 'text-blue-400' : 'text-gray-400'}`}
          >
            <ThumbsDown size={14} />
            <span>{argument.downvotes || 0}</span>
          </button>
        </div>
        
        <button 
          onClick={() => onReply(argument)}
          className="flex items-center space-x-1 text-xs text-gray-400 ml-auto"
        >
          <CornerUpRight size={14} />
          <span>Reply</span>
        </button>
      </div>
    </div>
  );
};

export default TeabateArgument;
